/**
 * CodeMirror 6 editor — the engine behind ctx.ui.codeEditor.
 *
 * Lives in the host (shell) so every app that calls ctx.ui.codeEditor shares one
 * CM6 instance through the SDK surface — no per-app bundling. Gives real
 * multi-cursor (⌘D → next occurrence, ⌥-click add cursor), move/duplicate/delete
 * line, comment toggle, and history out of the box (CM6 defaultKeymap), plus a
 * shared editor-preferences store (tab size, wrap, line numbers, font size).
 */
import { EditorState, Compartment, type Extension } from "@codemirror/state";
import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
  highlightActiveLineGutter,
  drawSelection,
  dropCursor,
  rectangularSelection,
  crosshairCursor,
  placeholder as placeholderExt,
} from "@codemirror/view";
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
  undo as cmUndo,
  redo as cmRedo,
} from "@codemirror/commands";
import { selectNextOccurrence, highlightSelectionMatches } from "@codemirror/search";
import {
  autocompletion,
  completionKeymap,
  type CompletionContext,
  type CompletionResult,
} from "@codemirror/autocomplete";
import {
  indentUnit,
  syntaxHighlighting,
  HighlightStyle,
  bracketMatching,
} from "@codemirror/language";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { markdown } from "@codemirror/lang-markdown";
import { python } from "@codemirror/lang-python";
import { tags as t } from "@lezer/highlight";

/* ── Shared editor preferences (persisted) ───────────────────────────────── */

export type EditorPrefs = {
  tabSize: number;
  wordWrap: boolean;
  lineNumbers: boolean;
  fontSize: number;
};

const PREFS_KEY = "arclog.editorPrefs";
const DEFAULTS: EditorPrefs = { tabSize: 2, wordWrap: false, lineNumbers: true, fontSize: 13 };

function loadPrefs(): EditorPrefs {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || "{}") };
  } catch {
    return { ...DEFAULTS };
  }
}

let prefs: EditorPrefs = loadPrefs();
const prefsSubs = new Set<(p: EditorPrefs) => void>();

export function getEditorPrefs(): EditorPrefs {
  return { ...prefs };
}

export function setEditorPrefs(patch: Partial<EditorPrefs>): EditorPrefs {
  prefs = {
    ...prefs,
    ...patch,
    tabSize: Math.max(1, Math.min(8, patch.tabSize ?? prefs.tabSize)),
    fontSize: Math.max(9, Math.min(28, patch.fontSize ?? prefs.fontSize)),
  };
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* private mode — keep in memory */
  }
  for (const fn of prefsSubs) fn(getEditorPrefs());
  return getEditorPrefs();
}

export function subscribeEditorPrefs(fn: (p: EditorPrefs) => void): () => void {
  prefsSubs.add(fn);
  return () => prefsSubs.delete(fn);
}

/* ── Theme + syntax colors (GitHub-dark-ish, transparent for glass) ───────── */

const THEME = EditorView.theme(
  {
    "&": { height: "100%", color: "#d7dde8", backgroundColor: "transparent" },
    ".cm-scroller": {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      lineHeight: "1.55",
    },
    ".cm-content": { padding: "10px 0" },
    ".cm-gutters": {
      backgroundColor: "transparent",
      color: "rgba(215,221,232,.30)",
      border: "none",
    },
    ".cm-lineNumbers .cm-gutterElement": { padding: "0 6px 0 12px" },
    ".cm-activeLine": { backgroundColor: "rgba(255,255,255,.035)" },
    ".cm-activeLineGutter": { backgroundColor: "rgba(255,255,255,.05)" },
    ".cm-cursor, .cm-dropCursor": { borderLeftColor: "var(--os-accent,#0a84ff)" },
    "&.cm-focused .cm-selectionBackground, .cm-selectionBackground": {
      backgroundColor: "rgba(10,132,255,.32)",
    },
    ".cm-selectionMatch": { backgroundColor: "rgba(255,255,255,.09)" },
    ".cm-matchingBracket, &.cm-focused .cm-matchingBracket": {
      backgroundColor: "rgba(10,132,255,.25)",
      outline: "1px solid rgba(10,132,255,.5)",
    },
    ".cm-placeholder": { color: "rgba(215,221,232,.35)" },
  },
  { dark: true }
);

const HIGHLIGHT = HighlightStyle.define([
  { tag: [t.keyword, t.controlKeyword, t.operatorKeyword, t.moduleKeyword], color: "#ff7b72" },
  { tag: [t.string, t.special(t.string), t.regexp], color: "#a5d6ff" },
  { tag: [t.comment, t.lineComment, t.blockComment], color: "#8b949e", fontStyle: "italic" },
  { tag: [t.number, t.bool, t.null, t.atom], color: "#79c0ff" },
  { tag: [t.function(t.variableName), t.function(t.propertyName), t.labelName], color: "#d2a8ff" },
  { tag: [t.typeName, t.className, t.namespace], color: "#7ee787" },
  { tag: [t.definition(t.variableName), t.propertyName], color: "#79c0ff" },
  { tag: [t.tagName], color: "#7ee787" },
  { tag: [t.attributeName], color: "#79c0ff" },
  { tag: [t.operator, t.punctuation, t.separator, t.bracket], color: "#c9d1d9" },
  { tag: [t.meta], color: "#8b949e" },
  { tag: t.invalid, color: "#ff7b72" },
]);

function languageExt(lang?: string): Extension | null {
  switch ((lang || "").toLowerCase()) {
    case "javascript":
    case "js":
    case "jsx":
      return javascript({ jsx: true });
    case "typescript":
    case "ts":
      return javascript({ typescript: true });
    case "tsx":
      return javascript({ typescript: true, jsx: true });
    case "json":
      return json();
    case "html":
    case "xml":
    case "svg":
      return html();
    case "css":
      return css();
    case "markdown":
    case "md":
      return markdown();
    case "python":
    case "py":
      return python();
    default:
      return null; // plain text — no highlighting
  }
}

/* ── Small settings gear + preferences popover ────────────────────────────── */

function ensureEditorCss() {
  if (typeof document === "undefined" || document.getElementById("arclog-ced-css")) return;
  const s = document.createElement("style");
  s.id = "arclog-ced-css";
  s.textContent = `
.os-code-ed{position:relative}
.os-code-ed .cm-editor{height:100%}
`;
  document.head.appendChild(s);
}

/* ── Editor factory ───────────────────────────────────────────────────────── */

/* ── Lightweight ctx.* IntelliSense (SDK surface completions) ─────────────── */

type Member = { label: string; detail?: string };
// Curated ModuleContext surface — enough to make ctx.* authoring feel like an
// IDE without shipping the whole TS language service into the browser.
const CTX_MEMBERS: Record<string, Member[]> = {
  ctx: [
    { label: "ui", detail: "UI kit (button, tree, codeEditor…)" },
    { label: "charts", detail: "canvas charts — line/area/bar/scatter/sparkline/statTile" },
    { label: "fs", detail: "virtual filesystem" },
    { label: "icons", detail: "Lucide icons" },
    { label: "shell", detail: "dialogs, menus, context menu, openPath" },
    { label: "launchParams", detail: "why this window opened — get() / subscribe()" },
    { label: "notify", detail: "toasts" },
    { label: "user", detail: "identity" },
    { label: "appState", detail: "persisted per-app KV" },
    { label: "docs", detail: "reopen last document" },
    { label: "windows", detail: "open / setTitle" },
    { label: "http", detail: "GET an allowlisted host via the OS proxy" },
    { label: "keys", detail: "has(id) / prompt(id) — user credentials; no getter, ever" },
    { label: "agents", detail: "shared per-account AI agents — run / jobs / message / memory / skills" },
    { label: "graphics", detail: "Three.js surfaces" },
    { label: "device", detail: "layout / presets" },
    { label: "time", detail: "clock + sun model (day/night)" },
    { label: "weather", detail: "current conditions — get() / subscribe(); 0..1 intensities" },
    { label: "workers", detail: "runParallel" },
    { label: "theme", detail: "ui scale / tokens" },
    { label: "widget", detail: "compact-tile API (widget mounts only): openAsApp / size / surface" },
    { label: "logs", detail: "structured OS logs — write / info / warn / error / query" },
    { label: "analytics", detail: "usage events — track(event, props). Metadata only, kept forever" },
    { label: "moduleVersion", detail: "string" },
  ],
  "ctx.keys": [
    { label: "has", detail: "(id) → Promise<boolean> — is this credential set?" },
    { label: "prompt", detail: "(id) — open Settings → Keys focused on this credential" },
  ],
  "ctx.agents": [
    { label: "list", detail: "() → Promise<AgentSummary[]> — fleet, personas never included" },
    { label: "run", detail: "({agentId,input,tools?,schema?,skills?,memory?}) → {jobId} — server-side job" },
    { label: "status", detail: "(jobId) → Promise<AgentJob|null>" },
    { label: "result", detail: "(jobId) → Promise<AgentJob|null>" },
    { label: "cancel", detail: "(jobId) → Promise<boolean>" },
    { label: "subscribe", detail: "(jobId, cb) → unsubscribe — job changes until terminal" },
    { label: "jobs", detail: "(agentId?, limit?) → Promise<AgentJob[]> newest first" },
    { label: "message", detail: "(agentId, text) — talk to an agent" },
    { label: "messages", detail: "(agentId?, since?) → transcript" },
    { label: "subscribeMessages", detail: "(cb) → unsubscribe — live fleet chatter" },
    { label: "memory", detail: "get / setFact / append — durable per-agent facts + journal" },
    { label: "skills", detail: "list / get(id) — markdown procedures loaded into prompts" },
    { label: "orchestrator", detail: "info() → who conducts this account's fleet" },
  ],
  "ctx.weather": [
    { label: "get", detail: "() → WeatherSnapshot | null — last reading, never throws" },
    { label: "subscribe", detail: "(fn) → off — called at once, then on change" },
    { label: "refresh", detail: "(opts?) → Promise — never rejects; stale on failure" },
    { label: "getLocation", detail: "() → string — the OS-wide city" },
    { label: "setLocation", detail: "(name) — point the whole OS at a city" },
    { label: "getError", detail: "() → string | null — why the last refresh failed" },
    { label: "setOverride", detail: "(patch|null) — preview a condition, like ctx.time" },
    { label: "getOverride", detail: "() → patch | null" },
  ],
  "ctx.wallpaper": [
    { label: "getActiveId", detail: "() → string — the live wallpaper's module id" },
    { label: "setActive", detail: "(id) → Promise — switch wallpaper" },
    { label: "setPaused", detail: "(bool) — freeze/resume animation" },
    { label: "list", detail: "() → string[] — installed kind:wallpaper ids" },
    {
      label: "settings",
      detail: "typed values from manifest.wallpaper.settings — get/getAll/set/reset/onChange",
    },
    {
      label: "pointer",
      detail: "get() → cursor snapshot; poll per frame. x/y are LAYOUT px, overDesktop gates it",
    },
    { label: "openSettings", detail: "(id?) — open the host's live settings panel" },
  ],
  "ctx.wallpaper.settings": [
    { label: "get", detail: "(key) → value — resolved against defaults and clamped" },
    { label: "getAll", detail: "() → Record<string, unknown>" },
    { label: "set", detail: "(key, value) — persists; remounts if the spec says reload" },
    { label: "reset", detail: "() — back to schema defaults" },
    {
      label: "onChange",
      detail:
        "(fn) → off — LIVE keys only; a reload key arrives as a fresh mount(); a type:\"button\" press arrives once as key:true, never persisted",
    },
  ],
  "ctx.analytics": [
    {
      label: "track",
      detail: "(event, props?) — usage metadata ONLY; stored verbatim & retained",
    },
    { label: "sessionId", detail: "() → string — the current visit id" },
  ],
  "ctx.charts": [
    {
      label: "create",
      detail: "(opts) → { el, setData, setSeries, resize, toTable, destroy }",
    },
    { label: "palette", detail: "(mode?) → the fixed 8 categorical hues, never cycled" },
    { label: "color", detail: "(slot 0..7, mode?) — throws outside range; no 9th hue" },
    { label: "status", detail: "(level, {label}) — reserved hues; label required" },
    { label: "sequential", detail: "(steps, {base}) — one hue light→dark" },
    { label: "diverging", detail: "(oddSteps, {low,high}) — two hues + grey midpoint" },
    { label: "mode", detail: "(el?) → 'light' | 'dark' from the live OS surface" },
    { label: "surface", detail: "(el?) → the opaque colour the chart sits on" },
    { label: "modeForSurface", detail: "(hex) → 'light' | 'dark'" },
    { label: "registerSeriesType", detail: "(renderer) — add a custom series shape" },
    { label: "onThemeChange", detail: "(fn) → unsubscribe (charts do this themselves)" },
  ],
  "ctx.ui": [
    { label: "button", detail: "(opts) → HTMLButtonElement" },
    { label: "slider", detail: "(opts) → value handle — range + live readout" },
    { label: "colorPicker", detail: "(opts) → value handle — swatch + hex" },
    { label: "toggle", detail: "(opts) → value handle — on/off switch" },
    { label: "select", detail: "(opts) → handle with setOptions" },
    { label: "numberField", detail: "(opts) → value handle — clamped number" },
    { label: "textField", detail: "(opts) → value handle — text (fires on blur)" },
    { label: "agentChatInput", detail: "(host, opts) — agent composer" },
    { label: "commandPalette", detail: "(opts) — ⌘⇧P palette" },
    { label: "tabs", detail: "(host, opts) — closable editor tabs" },
    { label: "statusBar", detail: "(host, opts) — IDE status strip" },
    { label: "tree", detail: "(host, opts)" },
    { label: "split", detail: "(host, opts)" },
    { label: "searchSelect", detail: "(host, opts)" },
    { label: "codeEditor", detail: "(host, opts) — CodeMirror" },
    { label: "richText", detail: "(host, opts)" },
    { label: "editorKeys", detail: "(textarea, opts)" },
    { label: "editorPrefs", detail: "get/set/subscribe/open" },
    { label: "sidebar", detail: "(host, opts)" },
    { label: "tooltip", detail: "attach/set" },
    { label: "selectable", detail: "(el, opts)" },
  ],
  "ctx.fs": [
    { label: "read", detail: "(path) → Promise<string>" },
    { label: "write", detail: "(path, content) → Promise" },
    { label: "list", detail: "(path, {showHidden?}) → Promise<entries> (dot-files hidden by default)" },
    { label: "stat", detail: "(path) → Promise<entry | null> (null = missing)" },
    { label: "mkdir", detail: "(path) → Promise" },
    { label: "remove", detail: "(path) → Promise" },
    { label: "move", detail: "(from, to) → Promise — into an existing dir; refuses to overwrite" },
    { label: "copy", detail: "(from, to) → Promise<entry> — never overwrites, auto-renames" },
  ],
  "ctx.shell": [
    { label: "dialog", detail: "alert/confirm/prompt/openFile…" },
    { label: "menus", detail: "set([...])" },
    { label: "openContextMenu", detail: "(x, y, items)" },
    {
      label: "openPath",
      detail: "(path) → {opened, appId, extension} — open a file in the app that handles it",
    },
  ],
  "ctx.launchParams": [
    { label: "get", detail: "() → {path?…} | undefined — why this window opened" },
    {
      label: "subscribe",
      detail: "(cb) → unsubscribe — fires when an OPEN window is re-launched",
    },
  ],
  "ctx.shell.dialog": [
    { label: "alert", detail: "(opts) → Promise<void>" },
    { label: "confirm", detail: "(opts) → Promise<boolean>" },
    { label: "prompt", detail: "(opts) → Promise<string|null>" },
    { label: "openFile", detail: "(opts) → Promise<string|null>" },
    { label: "openFolder", detail: "(opts) → Promise<string|null>" },
    { label: "saveFile", detail: "(opts) → Promise<string|null>" },
  ],
  "ctx.appState": [
    { label: "get", detail: "(key, fallback?)" },
    { label: "set", detail: "(key, value)" },
    { label: "remove", detail: "(key)" },
    { label: "namespace", detail: "string" },
  ],
  "ctx.docs": [
    { label: "last", detail: "() → RecentDocument|null" },
    { label: "setCurrent", detail: "(doc|path)" },
    { label: "clearCurrent", detail: "()" },
    { label: "recent", detail: "() → RecentDocument[]" },
    { label: "reopenOnLaunch", detail: "() → boolean" },
    { label: "setReopenOnLaunch", detail: "(on)" },
    { label: "restoreLast", detail: "(open) → boolean" },
  ],
  "ctx.icons": [
    { label: "create", detail: "(name, opts?) → SVGElement" },
    { label: "html", detail: "(name, opts?) → string" },
    { label: "resolve", detail: "(name) → string" },
    { label: "has", detail: "(name) → boolean" },
  ],
  "ctx.user": [{ label: "me", detail: "() → user | null" }],
  "ctx.windows": [
    { label: "open", detail: "(appId, opts?)" },
    { label: "close", detail: "(windowId)" },
    { label: "setTitle", detail: "(title)" },
    { label: "setDirty", detail: "(dirty, opts?) — confirm on close" },
    { label: "setCloseGuard", detail: "(fn | null) — veto close" },
    { label: "currentId", detail: "string" },
  ],
  "ctx.ui.editorPrefs": [
    { label: "get", detail: "() → prefs" },
    { label: "set", detail: "(patch)" },
    { label: "subscribe", detail: "(fn)" },
    { label: "open", detail: "() — prefs dialog" },
  ],
  "ctx.time": [
    { label: "now", detail: "() → Date" },
    { label: "get", detail: "() → TimeSnapshot (sunAltitude, phase…)" },
    { label: "subscribe", detail: "(fn, opts?) → unsubscribe" },
    { label: "setSunTimes", detail: "(sunriseHour, sunsetHour)" },
    { label: "setOverride", detail: "(hours | null) — preview a time" },
    { label: "getOverride", detail: "() → hours | null" },
  ],
  "ctx.widget": [
    { label: "openAsApp", detail: "() — open this module's full app window" },
    { label: "size", detail: "{ cols, rows } — current tile size" },
    { label: "surface", detail: '"desktop" | "panel"' },
    { label: "onResize", detail: "(cb) → unsubscribe — reserved; not yet fired" },
  ],
  "ctx.graphics": [
    { label: "backend", detail: '"three" | "canvas2d"' },
    { label: "three", detail: "THREE singleton | null" },
    { label: "createSurface", detail: "(el, opts?) → surface" },
    {
      label: "createShaderQuad",
      detail: "(el, opts) — shader background on any element; 1 WebGL context each",
    },
    {
      label: "create2D",
      detail:
        "(el, {width, height, scaling, pixelated, keyMap}) → surface — fixed-resolution 2D canvas. Host owns letterbox, frame loop, focus-scoped input, disposal. No DPR scaling, so pixel art stays sharp.",
    },
    {
      label: "exportMesh",
      detail:
        "(pieces, {format:'glb'|'obj', mode, center, scale, name}) → {files:[{name,blob}], info} — glTF/OBJ writer. Plain data in, never a THREE object. OBJ returns TWO files.",
    },
    {
      label: "loadTexture",
      detail:
        "(url, opts?) → {texture, ready} — texture is usable at once; heightmap defaults (clamp, linear, no mipmaps, no sRGB). Pass it in ownedTextures.",
    },
    {
      label: "glsl",
      detail: "shared GLSL chunk source: hash / noise / fbm / stars",
    },
    { label: "getSurface", detail: "(id) → surface | null" },
    { label: "disposeSurface", detail: "(id)" },
  ],
  "ctx.graphics.glsl": [
    { label: "hash", detail: "hash11(float), hash21(vec2)" },
    { label: "noise", detail: "valueNoise(vec2)" },
    { label: "fbm", detail: "fbm(vec2) — 5 octaves of value noise" },
    {
      label: "stars",
      detail: "stars(p, scale, t, seed) — round twinkling points, layer 2-3 scales",
    },
  ],
};

function ctxCompletionSource(context: CompletionContext): CompletionResult | null {
  const word = context.matchBefore(/[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*\.?[A-Za-z_$]*$/);
  if (!word || word.text.indexOf(".") < 0) return null;
  const lastDot = word.text.lastIndexOf(".");
  const objPath = word.text.slice(0, lastDot);
  const partial = word.text.slice(lastDot + 1);
  const members = CTX_MEMBERS[objPath];
  if (!members) return null;
  return {
    from: word.from + lastDot + 1,
    options: members
      .filter((m) => !partial || m.label.startsWith(partial))
      .map((m) => ({ label: m.label, detail: m.detail, type: "property" })),
    validFor: /^[\w$]*$/,
  };
}

export type CodeEditorOpts = {
  value?: string;
  language?: string;
  readOnly?: boolean;
  placeholder?: string;
  className?: string;
  onChange?: (value: string) => void;
  onSave?: (value: string) => void;
  /** Caret moved or the document changed — wire this to a status bar. */
  onCursor?: (pos: CodeEditorCursor) => void;
};

/** 1-based caret position plus how many cursors/selected chars are live. */
export type CodeEditorCursor = {
  line: number;
  col: number;
  /** Number of cursors (multi-cursor selections count separately). */
  cursors: number;
  /** Total selected characters across all selections. */
  selected: number;
};

export type CodeEditorHandle = {
  el: HTMLElement;
  getValue: () => string;
  setValue: (v: string) => void;
  setLanguage: (l: string) => void;
  /** Move the caret to a 1-based line/column and scroll it into view. */
  goTo: (line: number, col?: number) => void;
  /** Current caret position — the same shape `onCursor` receives. */
  getCursor: () => CodeEditorCursor;
  /** Undo/redo the editor's own history. Returns false if there was nothing to do. */
  undo: () => boolean;
  redo: () => boolean;
  focus: () => void;
  destroy: () => void;
};

export function createCodeEditor(host: HTMLElement, opts: CodeEditorOpts = {}): CodeEditorHandle {
  ensureEditorCss();
  host.innerHTML = "";
  let lang = opts.language || "typescript";

  const root = document.createElement("div");
  root.className = "os-code-ed" + (opts.className ? " " + opts.className : "");

  const bar = document.createElement("div");
  bar.className = "os-code-ed-bar";
  const langLabel = document.createElement("span");
  langLabel.textContent = lang;
  bar.append(langLabel);

  const body = document.createElement("div");
  body.className = "os-code-ed-body";
  root.append(bar, body);
  host.appendChild(root);

  const langComp = new Compartment();
  const tabComp = new Compartment();
  const wrapComp = new Compartment();
  const gutterComp = new Compartment();
  const fontComp = new Compartment();

  const p0 = getEditorPrefs();
  const tabExt = (n: number) => [EditorState.tabSize.of(n), indentUnit.of(" ".repeat(n))];
  const gutterExt = (on: boolean) =>
    on ? [lineNumbers(), highlightActiveLineGutter()] : [];
  const fontExt = (px: number) => EditorView.theme({ ".cm-scroller": { fontSize: px + "px" } });

  const view = new EditorView({
    parent: body,
    doc: opts.value || "",
    extensions: [
      history(),
      drawSelection(),
      dropCursor(),
      EditorState.allowMultipleSelections.of(true),
      rectangularSelection(),
      crosshairCursor(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      bracketMatching(),
      autocompletion({ override: [ctxCompletionSource], activateOnTyping: true }),
      syntaxHighlighting(HIGHLIGHT),
      THEME,
      gutterComp.of(gutterExt(p0.lineNumbers)),
      tabComp.of(tabExt(p0.tabSize)),
      wrapComp.of(p0.wordWrap ? EditorView.lineWrapping : []),
      fontComp.of(fontExt(p0.fontSize)),
      langComp.of(languageExt(lang) || []),
      placeholderExt(opts.placeholder || ""),
      EditorState.readOnly.of(!!opts.readOnly),
      EditorView.editable.of(!opts.readOnly),
      keymap.of([
        { key: "Mod-d", run: selectNextOccurrence, preventDefault: true },
        {
          key: "Mod-s",
          preventDefault: true,
          run: (v) => {
            opts.onSave?.(v.state.doc.toString());
            return true;
          },
        },
        indentWithTab,
        ...completionKeymap,
        ...defaultKeymap,
        ...historyKeymap,
      ]),
      EditorView.updateListener.of((u) => {
        if (u.docChanged) opts.onChange?.(u.state.doc.toString());
        if (u.docChanged || u.selectionSet) opts.onCursor?.(readCursor());
      }),
    ],
  });

  function readCursor(): CodeEditorCursor {
    const sel = view.state.selection;
    const head = sel.main.head;
    const line = view.state.doc.lineAt(head);
    let selected = 0;
    for (const r of sel.ranges) selected += Math.abs(r.to - r.from);
    return {
      line: line.number,
      col: head - line.from + 1,
      cursors: sel.ranges.length,
      selected,
    };
  }

  // Reconfigure live when shared preferences change (edited via the Editor
  // Preferences dialog — see ctx.ui.editorPrefs.open()).
  const unsubPrefs = subscribeEditorPrefs((p) => {
    view.dispatch({
      effects: [
        tabComp.reconfigure(tabExt(p.tabSize)),
        wrapComp.reconfigure(p.wordWrap ? EditorView.lineWrapping : []),
        gutterComp.reconfigure(gutterExt(p.lineNumbers)),
        fontComp.reconfigure(fontExt(p.fontSize)),
      ],
    });
  });

  return {
    el: root,
    getValue: () => view.state.doc.toString(),
    setValue(v: string) {
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: v } });
    },
    setLanguage(l: string) {
      lang = l;
      langLabel.textContent = l;
      view.dispatch({ effects: langComp.reconfigure(languageExt(l) || []) });
    },
    goTo(line: number, col = 1) {
      const total = view.state.doc.lines;
      const ln = view.state.doc.line(Math.max(1, Math.min(line, total)));
      const pos = Math.min(ln.from + Math.max(0, col - 1), ln.to);
      view.dispatch({ selection: { anchor: pos }, scrollIntoView: true });
      view.focus();
    },
    getCursor: readCursor,
    undo: () => cmUndo(view),
    redo: () => cmRedo(view),
    focus: () => view.focus(),
    destroy() {
      unsubPrefs();
      view.destroy();
      root.remove();
    },
  };
}
